import dayjs from 'dayjs'
import React, { ReactElement } from 'react'
import { RemoteLogger } from '@ally/federated-types'
import {
  StatusTrackerActivity,
  WireEvent,
  DebitCardOrderEvent,
} from '@ally/shared-status-tracker'
import { AchEvent } from './StatusTrackerTips/types'
import { SERVER_TIMEZONE } from '../../constants'

export const checkOrderLR = (
  currentStatus: string,
  currentTime: string,
  previousStatus: string,
  previousTime: string,
  log: RemoteLogger,
): void => {
  const inProcess = currentStatus && currentStatus === 'InProcess'
  const confirmed = previousStatus && previousStatus === 'Confirmed'
  if (inProcess && confirmed) {
    const confirmedTime = previousTime
    const inProcessTime = currentTime
    if (dayjs(inProcessTime).diff(confirmedTime, 'hours') > 24) {
      log.track('status-tracker-checkorder-date-diff')
    }
  }
}

/**
 * Function that checks if ACH transfer is past deliverOnDate
 *
 * @param {string} dateString
 * @returns {boolean}
 *
 * @example
 * const deliverOnDateTime = '2020-09-02T10:58:29.486Z'
 */
export function isAfterDeliverOnDate(deliverOnDateTime: string): boolean {
  const today = dayjs().tz(SERVER_TIMEZONE)
  const deliverOnDate = dayjs(deliverOnDateTime).tz(SERVER_TIMEZONE)
  return today.isAfter(deliverOnDate, 'day')
}

// Status Tracker
function showWire(
  currentStatus: string,
  daysSinceStatusChange: number,
): boolean {
  if (currentStatus === 'Canceled' || currentStatus === 'Sent') {
    return daysSinceStatusChange <= 2
  }
  return true
}

function showCheckOrder(
  log: RemoteLogger,
  currentStatus: string,
  currentTime: string,
  daysSinceStatusChange: number,
  previousStatus?: string,
  previousTime?: string,
): boolean {
  checkOrderLR(currentStatus, currentTime, previousStatus!, previousTime!, log)
  if (currentStatus === 'Shipped') {
    return daysSinceStatusChange <= 7
  }
  return true
}

function showAch(
  currentStatus: string,
  daysSinceStatusChange: number,
): boolean {
  if (currentStatus === 'Completed') {
    return daysSinceStatusChange <= 2
  }
  // ACH returns many status' we do not need so only return what we care for
  return currentStatus === 'InProcess'
}

export const filterActivities = (
  activities: StatusTrackerActivity[],
  log: RemoteLogger,
): StatusTrackerActivity[] => {
  // Converting all ACH activities afterDeliverOnDate to 'Completed' for sorting purposes
  const mappedActivities = activities.map(activity => {
    const isAch = activity.activityName === 'ACHTransfer'
    const isWire = activity.activityName === 'WireTransfer'
    const isDebitCardOrder = activity.activityName === 'DebitCardOrder'
    const currentStatus = activity.statuses[0].status
    const today = dayjs().tz(SERVER_TIMEZONE)
    const deliverOnDateTime = (activity.eventModelData as AchEvent)
      .deliverOnDate
    const afterDeliverOnDate = isAfterDeliverOnDate(deliverOnDateTime)

    if (isAch && currentStatus === 'InProcess' && afterDeliverOnDate) {
      const deliverOnDatePlusOne = dayjs(deliverOnDateTime)
        .tz(SERVER_TIMEZONE)
        .add(1, 'day')
      const timeSinceStatusChange = today.diff(deliverOnDatePlusOne, 'day')

      return {
        ...activity,
        completed: true,
        timeSinceStatusChange,
        statuses: [{ ...activity.statuses[0], status: 'Completed' }],
      }
    }
    if (isWire) {
      const recipientRoutingNumber =
        (activity.eventModelData as WireEvent).recipientRoutingNumber || '--'

      return {
        ...activity,
        eventModelData: {
          ...activity.eventModelData,
          recipientRoutingNumber,
        } as WireEvent,
      }
    }

    if (isDebitCardOrder) {
      return {
        ...activity,
        eventModelData: {
          ...activity.eventModelData,
        } as DebitCardOrderEvent,
      }
    }

    return activity
  }) as StatusTrackerActivity[]

  return mappedActivities.filter((activity: StatusTrackerActivity) => {
    const currentStatus = activity.statuses[0].status
    const previousStatus = activity.statuses[1]?.status
    const currentTime = activity.statuses[0].eventDateTime
    const previousTime = activity.statuses[1]?.eventDateTime
    const daysSinceStatusChange = activity.timeSinceStatusChange

    switch (activity.activityName) {
      case 'WireTransfer':
        return showWire(currentStatus, daysSinceStatusChange)

      case 'CheckOrder':
        return showCheckOrder(
          log,
          currentStatus,
          currentTime,
          daysSinceStatusChange,
          previousStatus,
          previousTime,
        )

      case 'DebitCardOrder':
        return true
      case 'ACHTransfer':
      default:
        return showAch(currentStatus, daysSinceStatusChange)
    }
  })
}

/**
 * Sorts wire transfer activities based on priority of status
 */
// eslint-disable-next-line complexity
export function sortWireTransfer(
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): number {
  const activity1CurrentStatus = activity1.statuses[0]
  const activity2CurrentStatus = activity2.statuses[0]
  switch (true) {
    case activity1.activityName === 'WireTransfer' &&
      activity2.activityName !== 'WireTransfer':
      return -1
    case activity1.activityName !== 'WireTransfer' &&
      activity2.activityName === 'WireTransfer':
      return 1
    case (activity1.eventModelData as WireEvent).contactUsFlag &&
      !(activity1.eventModelData as WireEvent).contactUsFlag:
      return -1
    case !(activity1.eventModelData as WireEvent).contactUsFlag &&
      (activity1.eventModelData as WireEvent).contactUsFlag:
      return 1
    case activity1CurrentStatus.status === 'VerificationRequired' &&
      activity2CurrentStatus.status !== 'VerificationRequired':
      return -1
    case activity1CurrentStatus.status !== 'VerificationRequired' &&
      activity2CurrentStatus.status === 'VerificationRequired':
      return 1
    case activity1CurrentStatus.status === 'Canceled' &&
      activity2CurrentStatus.status !== 'Canceled':
      return -1
    case activity1CurrentStatus.status !== 'Canceled' &&
      activity2CurrentStatus.status === 'Canceled':
      return 1
    case activity1CurrentStatus.status === 'Requested' &&
      activity2CurrentStatus.status !== 'Requested':
      return -1
    case activity1CurrentStatus.status !== 'Requested' &&
      activity2CurrentStatus.status === 'Requested':
      return 1
    case activity1CurrentStatus.status === 'Reviewing' &&
      activity2CurrentStatus.status !== 'Reviewing':
      return -1
    case activity1CurrentStatus.status !== 'Reviewing' &&
      activity2CurrentStatus.status === 'Reviewing':
      return 1
    case activity1CurrentStatus.status === 'Processing' &&
      activity2CurrentStatus.status !== 'Processing':
      return -1
    case activity1CurrentStatus.status !== 'Processing' &&
      activity2CurrentStatus.status === 'Processing':
      return 1
    case activity1CurrentStatus.status === 'Sent' &&
      activity2CurrentStatus.status !== 'Sent':
      return -1
    case activity1CurrentStatus.status !== 'Sent' &&
      activity2CurrentStatus.status === 'Sent':
      return 1
    default:
      return 0
  }
}

/**
 * Sorts check order activities based on priority of status
 */
export function sortCheckOrder(
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): number {
  const activity1CurrentStatus = activity1.statuses[0]
  const activity2CurrentStatus = activity2.statuses[0]
  switch (true) {
    case activity1.activityName === 'CheckOrder' &&
      activity2.activityName !== 'CheckOrder':
      return -1
    case activity1.activityName !== 'CheckOrder' &&
      activity2.activityName === 'CheckOrder':
      return 1
    case activity1CurrentStatus.status === 'InProcess' &&
      activity2CurrentStatus.status !== 'InProcess':
      return -1
    case activity1CurrentStatus.status !== 'InProcess' &&
      activity2CurrentStatus.status === 'InProcess':
      return 1
    case activity1.activityName === 'CheckOrder' &&
      activity1CurrentStatus.status === 'Shipped':
      return 1
    case activity2.activityName === 'CheckOrder' &&
      activity2CurrentStatus.status === 'Shipped':
      return -1
    default:
      return 0
  }
}

/**
 * Sorts ACH transfer activities based on priority of status
 */
export function sortACH(
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): number {
  const activity1CurrentStatus = activity1.statuses[0]
  const activity2CurrentStatus = activity2.statuses[0]
  switch (true) {
    case (activity1.activityName === 'CheckOrder' &&
      activity1CurrentStatus.status === 'InProcess') ||
      (activity2.activityName === 'CheckOrder' &&
        activity2CurrentStatus.status === 'InProcess'):
      return 0
    case activity1.activityName === 'ACHTransfer' &&
      activity2.activityName !== 'ACHTransfer':
      return -1
    case activity1.activityName !== 'ACHTransfer' &&
      activity2.activityName === 'ACHTransfer':
      return 1
    case activity1CurrentStatus.status === 'InProcess' &&
      activity2CurrentStatus.status !== 'InProcess':
      return -1
    case activity1CurrentStatus.status !== 'InProcess' &&
      activity2CurrentStatus.status === 'InProcess':
      return 1
    case activity1CurrentStatus.status === 'Completed' &&
      activity2CurrentStatus.status !== 'Completed':
      return -1
    case activity1CurrentStatus.status !== 'Completed' &&
      activity2CurrentStatus.status === 'Completed':
      return 1
    default:
      return 0
  }
}

/**
 * Fetches all status tracker activity and returns an array
 * of status tracker activities ordered by business logic rules
 *
 * ORDER OF PRIORITY
 * Wire - contactUsFlag
 * Wire - Needs Attention
 * Wire - Canceled (show if <2 days since canceled status, hide if ≥2 days)
 * Wire - all other statuses
 *        Requested
 *        Reviewing
 *        Processing
 * Sent wires - (show if <2 days since sent status, hide if ≥2 days)
 * Check Order – Ordered
 * ACH – Processing
 * ACH - Delivered (show if <2 days since delivered status, hide if ≥2 days)
 * Check Order - Shipped (show if <7 days since shipped status hide if ≥7 days)
 */
export function sortActivities(
  activities: StatusTrackerActivity[],
): StatusTrackerActivity[] {
  /**
   * Sorts activities based on activity name and status
   */
  const sortedActivities = activities.sort(
    (activity1: StatusTrackerActivity, activity2: StatusTrackerActivity) => {
      if (
        activity1.activityName === 'WireTransfer' ||
        activity2.activityName === 'WireTransfer'
      ) {
        const sort = sortWireTransfer(activity1, activity2)
        if (sort) {
          return sort
        }
      }

      if (
        (activity1.activityName === 'CheckOrder' &&
          activity1.status === 'InProcess') ||
        (activity2.activityName === 'CheckOrder' &&
          activity2.status === 'InProcess')
      ) {
        const sort = sortCheckOrder(activity1, activity2)
        if (sort) {
          return sort
        }
      }

      if (
        activity1.activityName === 'ACHTransfer' ||
        activity2.activityName === 'ACHTransfer'
      ) {
        const sort = sortACH(activity1, activity2)
        if (sort) {
          return sort
        }
      }

      if (
        (activity1.activityName === 'CheckOrder' &&
          activity1.status !== 'InProcess') ||
        (activity2.activityName === 'CheckOrder' &&
          activity2.status !== 'InProcess')
      ) {
        const sort = sortCheckOrder(activity1, activity2)
        if (sort) {
          return sort
        }
      }

      return 0
    },
  ) as StatusTrackerActivity[]

  return sortedActivities
}

/**
 * Adds tradmark superscript to strings based on a keyword
 *
 * Logic
 * 1. Find index of tradeMarkedWord
 * 2. Split fullString based on index + length of tradeMarkedWord
 * 3. Return formatted string with Trademark
 */
export function addTradeMark(
  fullString: string,
  tradeMarkedWord: string,
): ReactElement {
  if (!fullString.includes(tradeMarkedWord)) return <>{fullString}</>
  const index = fullString.indexOf(tradeMarkedWord)
  const beginning = fullString.slice(0, index + tradeMarkedWord.length)
  const ending = fullString.slice(index + tradeMarkedWord.length)

  return (
    <>
      {beginning}
      <sup>®</sup>
      {ending}
    </>
  )
}
